import type { Card, CardPair, Player, Winner } from './types';
import { PAIRS_PER_DECK } from './types';

export const FARM_COLORS = [
  'text-red-500',
  'text-blue-500',
  'text-yellow-500',
  'text-purple-500',
  'text-pink-500',
] as const;

export const BG_CLASSES = [
  'bg-red-50',
  'bg-blue-50',
  'bg-yellow-50',
  'bg-purple-50',
  'bg-pink-50',
] as const;

export const INITIAL_FRUITS = 3;

export function shuffle<T>(arr: readonly T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = out[i]!;
    const b = out[j]!;
    out[i] = b;
    out[j] = a;
  }
  return out;
}

export function buildDeck(pairs: readonly CardPair[]): Card[] {
  const deck: Card[] = [];
  pairs.forEach((pair, idx) => {
    deck.push({
      id: `q-${idx}`,
      pairId: idx,
      content: pair.q,
      side: 'q',
      isFlipped: false,
      isMatched: false,
    });
    deck.push({
      id: `a-${idx}`,
      pairId: idx,
      content: pair.a,
      side: 'a',
      isFlipped: false,
      isMatched: false,
    });
  });
  return shuffle(deck);
}

export function buildPlayers(count: number, names: readonly string[]): Player[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    name: names[i] || `農夫 ${i + 1}`,
    fruits: INITIAL_FRUITS,
    isEliminated: false,
    farmColor: FARM_COLORS[i % FARM_COLORS.length]!,
    bgClass: BG_CLASSES[i % BG_CLASSES.length]!,
  }));
}

export function isMatch(cards: readonly Card[], indexA: number, indexB: number): boolean {
  const a = cards[indexA];
  const b = cards[indexB];
  if (!a || !b) return false;
  return a.pairId === b.pairId;
}

export function nextActiveIndex(players: readonly Player[], current: number): number {
  if (players.length === 0) return 0;
  let next = current;
  for (let i = 0; i < players.length; i++) {
    next = (next + 1) % players.length;
    if (!players[next]!.isEliminated) return next;
  }
  return current; // 全部淘汰（理論上不會發生，CHECK_WIN 會先收尾）
}

export function getStealTargets(
  players: readonly Player[],
  currentTurn: number,
  lastVictimId: number | null,
): Player[] {
  const activeCount = players.filter((p) => !p.isEliminated).length;
  const ignoreProtection = activeCount <= 2;
  return players.filter((p) => {
    if (p.id === currentTurn || p.isEliminated || p.fruits <= 0) return false;
    if (!ignoreProtection && p.id === lastVictimId) return false;
    return true;
  });
}

export function checkWinner(players: readonly Player[]): Winner | null {
  const active = players.filter((p) => !p.isEliminated);
  if (active.length <= 1 && players.length > 0) {
    return { type: 'survivor', players: [...active] };
  }
  return null;
}

// AI 出題後規範化：保證長度為 PAIRS_PER_DECK
export function normalizePairs(raw: unknown): CardPair[] {
  if (!Array.isArray(raw)) return emptyPairs();
  const pairs: CardPair[] = raw.slice(0, PAIRS_PER_DECK).map((p: unknown) => {
    const obj = (p ?? {}) as { q?: unknown; a?: unknown };
    return {
      q: typeof obj.q === 'string' ? obj.q : '',
      a: typeof obj.a === 'string' ? obj.a : '',
    };
  });
  while (pairs.length < PAIRS_PER_DECK) pairs.push({ q: '', a: '' });
  return pairs;
}

// 空白 8 對（setup 初始狀態）
export function emptyPairs(): CardPair[] {
  return Array.from({ length: PAIRS_PER_DECK }, () => ({ q: '', a: '' }));
}
